import { toUserMessage } from '../../lib/errors.js';
import {
  IonActionSheet,
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFab,
  IonFabButton,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonText,
  IonToggle,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { add, closeOutline, copyOutline, ellipsisVertical, keyOutline, mailOutline, personOutline, tabletLandscapeOutline, trashOutline } from 'ionicons/icons';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useEnqueueMaster, useMasters, useTenantAccounts, type TenantAccount } from '../../lib/data/queries.js';
import { getSupabase } from '../../lib/supabase.js';
import { shareWithNavigator } from '../../lib/adapters/share/index.js';
import {
  fetchSharedTabletOperators,
  fetchSharedTabletPinRequired,
  generateTenantPersonnelPin,
  isOperatorPinNotRecoverable,
  revealTenantPersonnelPin,
  revokeTenantPersonnelPin,
  setMyTenantPersonnelPin,
  setSharedTabletPinRequired,
  type VisibleOperatorPin,
} from '../../lib/auth/sharedTabletOperatorApi.js';
import type { SharedTabletOperator } from '../../lib/auth/sharedTabletOperatorTypes.js';
import { canManagePersonnel, hasAppCapability } from '../../lib/auth/capabilities.js';
import { OperatorPinModal } from '../../components/OperatorPinModal.js';

interface StaffProfile {
  id: string;
  tenant_id: string;
  user_id: string;
  display_name: string;
  kitchen_role: string;
  attestato_haccp_scadenza: string | null;
  archived_at: string | null;
  version?: number;
}

// Ruoli predefiniti per il dropdown. L'utente può inserire un valore custom
// scegliendo "Altro..." → input free-form (il campo DB è text, non enum).
const PREDEFINED_KITCHEN_ROLES: { value: string; label: string }[] = [
  { value: 'Titolare',                label: '👔 Titolare / OSA' },
  { value: 'Responsabile HACCP',      label: '📋 Responsabile HACCP' },
  { value: 'Cuoco',                   label: '👨‍🍳 Cuoco / Chef' },
  { value: 'Aiuto cuoco',             label: '🍳 Aiuto cuoco' },
  { value: 'Pizzaiolo',               label: '🍕 Pizzaiolo' },
  { value: 'Pasticcere',              label: '🧁 Pasticcere' },
  { value: 'Lavapiatti',              label: '🧽 Lavapiatti' },
  { value: 'Responsabile sala',       label: '🎩 Responsabile sala / Maître' },
  { value: 'Cameriere',               label: '🍽️ Cameriere' },
  { value: 'Barista',                 label: '☕ Barista' },
  { value: 'Addetto pulizie',         label: '🧹 Addetto pulizie' },
  { value: 'Magazziniere',            label: '📦 Magazziniere' },
];

// T-306: badge per il PERMESSO APP (diverso dal ruolo-cucina). Etichette IT.
const APP_ROLE_BADGE: Record<string, { label: string; bg: string; fg: string }> = {
  ristoratore: { label: 'Ristoratore', bg: '#dcfce7', fg: '#15803d' },
  osa: { label: 'OSA', bg: '#dbeafe', fg: '#1d4ed8' },
  consulente: { label: 'Consulente HACCP', bg: '#ede9fe', fg: '#6d28d9' },
  staff: { label: 'Staff', bg: '#f1f5f9', fg: '#475569' },
  preposto: { label: 'Preposto', bg: '#fef3c7', fg: '#b45309' },
};

export function StaffPage(): JSX.Element {
  const { profile } = useAuth();
  const history = useHistory();
  const { data: staff = [] } = useMasters<StaffProfile>('staff_profiles');
  const enqueue = useEnqueueMaster();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<StaffProfile | null>(null);
  const [form, setForm] = useState({ display_name: '', kitchen_role: 'Cuoco', kitchen_role_custom: '', attestato: '' });
  const [confirmArchive, setConfirmArchive] = useState<StaffProfile | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Modal invito email
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', display_name: '', kitchen_role: 'Cuoco', kitchen_role_custom: '', app_role: 'staff' as 'consulente' | 'ristoratore' | 'osa' | 'staff', attestato: '' });
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteSending, setInviteSending] = useState(false);

  const isAdmin = canManagePersonnel(profile);
  const canManageTabletMode = hasAppCapability(profile, 'shared_tablet.manage');
  // T-306: account-login del locale (email + permesso app). Solo admin → enabled=isAdmin.
  const { data: accounts = [], isLoading: accountsLoading } = useTenantAccounts(isAdmin);
  const qc = useQueryClient();
  const [actionAccount, setActionAccount] = useState<TenantAccount | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<TenantAccount | null>(null);
  const [visiblePin, setVisiblePin] = useState<VisibleOperatorPin | null>(null);
  const [pinBusyId, setPinBusyId] = useState<string | null>(null);
  const [confirmPinRevoke, setConfirmPinRevoke] = useState<SharedTabletOperator | null>(null);
  const [confirmLegacyPinRegeneration, setConfirmLegacyPinRegeneration] = useState<SharedTabletOperator | null>(null);
  const [myPin, setMyPin] = useState('');
  const [savingMyPin, setSavingMyPin] = useState(false);
  const [savingPinPolicy, setSavingPinPolicy] = useState(false);
  const operatorRoster = useQuery({
    queryKey: ['shared_tablet_operators', profile?.active_tenant_id],
    enabled: isAdmin && !!profile?.active_tenant_id,
    queryFn: fetchSharedTabletOperators,
    staleTime: 30_000,
  });
  const pinPolicy = useQuery({
    queryKey: ['shared_tablet_pin_policy', profile?.active_tenant_id],
    enabled: canManageTabletMode && !!profile?.active_tenant_id,
    queryFn: fetchSharedTabletPinRequired,
  });

  async function runAccountAction(acc: TenantAccount, action: 'set_role' | 'remove', role?: string): Promise<void> {
    if (!profile?.active_tenant_id) return;
    try {
      const { error } = await getSupabase().rpc('tenant_account_update', {
        p_tenant_id: profile.active_tenant_id,
        p_user_id: acc.user_id,
        p_action: action,
        p_role: role ?? null,
      });
      if (error) throw error;
      void qc.invalidateQueries({ queryKey: ['tenant_accounts'] });
      setToast(action === 'remove' ? 'Accesso rimosso' : 'Permesso aggiornato');
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function setAccountAccessMode(
    account: TenantAccount,
    accessMode: 'personal' | 'shared_tablet',
  ): Promise<void> {
    try {
      const { error } = await getSupabase().rpc('set_tenant_account_access_mode_v1', {
        p_user_id: account.user_id,
        p_access_mode: accessMode,
      });
      if (error) throw error;
      await qc.invalidateQueries({ queryKey: ['tenant_accounts'] });
      await qc.invalidateQueries({ queryKey: ['active_tenant_account_context'] });
      setToast(accessMode === 'shared_tablet'
        ? 'Account trasformato in tablet staff condiviso'
        : 'Modalità tablet disattivata');
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function generateOperatorPin(personnelId: string): Promise<void> {
    setPinBusyId(personnelId);
    try {
      const result = await generateTenantPersonnelPin(personnelId);
      setVisiblePin(result);
      setToast(result.email_sent
        ? `PIN inviato via email a ${result.full_name}`
        : 'PIN generato: consegnalo manualmente alla persona interessata');
      await qc.invalidateQueries({ queryKey: ['shared_tablet_operators'] });
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setPinBusyId(null);
    }
  }

  async function showOperatorPin(operator: SharedTabletOperator): Promise<void> {
    setPinBusyId(operator.tenant_personnel_id);
    try {
      setVisiblePin(await revealTenantPersonnelPin(operator.tenant_personnel_id));
    } catch (err) {
      if (isOperatorPinNotRecoverable(err)) setConfirmLegacyPinRegeneration(operator);
      else setToast(toUserMessage(err));
    } finally {
      setPinBusyId(null);
    }
  }

  async function revokeOperatorPin(operator: SharedTabletOperator): Promise<void> {
    setPinBusyId(operator.tenant_personnel_id);
    try {
      await revokeTenantPersonnelPin(operator.tenant_personnel_id);
      setToast(`PIN di ${operator.full_name} revocato`);
      await qc.invalidateQueries({ queryKey: ['shared_tablet_operators'] });
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setPinBusyId(null);
      setConfirmPinRevoke(null);
    }
  }

  async function saveMyPin(): Promise<void> {
    if (!/^\d{4}$/.test(myPin)) return;
    setSavingMyPin(true);
    try {
      setVisiblePin(await setMyTenantPersonnelPin(myPin));
      setMyPin('');
      setToast('PIN personale aggiornato');
      await qc.invalidateQueries({ queryKey: ['shared_tablet_operators'] });
    } catch (err) {
      setToast(toUserMessage(err));
    } finally { setSavingMyPin(false); }
  }

  async function changePinPolicy(required: boolean): Promise<void> {
    setSavingPinPolicy(true);
    try {
      await setSharedTabletPinRequired(required);
      await qc.invalidateQueries({ queryKey: ['shared_tablet_pin_policy'] });
      setToast(required ? 'PIN richiesto sul tablet' : 'Accesso senza PIN abilitato sul tablet');
    } catch (err) { setToast(toUserMessage(err)); }
    finally { setSavingPinPolicy(false); }
  }

  const visibleStaff = useMemo(
    () => staff.filter((s) => !s.archived_at && s.tenant_id === profile?.active_tenant_id),
    [staff, profile?.active_tenant_id],
  );

  function openPersonnelTraining(): void {
    history.push('/haccp/formazione?new=1&from=staff');
  }

  function openEdit(s: StaffProfile): void {
    setEditing(s);
    const isPredef = PREDEFINED_KITCHEN_ROLES.some((r) => r.value === s.kitchen_role);
    setForm({
      display_name: s.display_name,
      kitchen_role: isPredef ? s.kitchen_role : '__custom__',
      kitchen_role_custom: isPredef ? '' : s.kitchen_role,
      attestato: s.attestato_haccp_scadenza ?? '',
    });
    setEditorOpen(true);
  }

  async function onSave(): Promise<void> {
    if (!profile?.active_tenant_id) { setToast('Nessun tenant attivo'); return; }
    if (!editing) {
      // CREATE: serve un user_id. Per ora, creazione senza invito = self-only
      // (il proprietario aggiunge se stesso o duplicati). Il flusso completo
      // di invito è in invite-staff edge function (Fase 1.3).
      setToast('Per aggiungere altri utenti usa il bottone "Invita via email" (in arrivo).');
      return;
    }
    const role = form.kitchen_role === '__custom__' ? form.kitchen_role_custom.trim() : form.kitchen_role;
    if (!role) { setToast('Indica il ruolo'); return; }
    const name = form.display_name.trim();
    if (!name) { setToast('Indica il nome'); return; }
    try {
      await enqueue.mutateAsync({
        table: 'staff_profiles',
        id: editing.id,
        op: 'update',
        base_version: editing.version ?? 0,
        patch: {
          id: editing.id,
          tenant_id: editing.tenant_id,
          user_id: editing.user_id,
          display_name: name,
          kitchen_role: role,
          attestato_haccp_scadenza: form.attestato || null,
        },
      });
      setToast('Staff aggiornato');
      setEditorOpen(false);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function sendInvite(): Promise<void> {
    const role = inviteForm.kitchen_role === '__custom__' ? inviteForm.kitchen_role_custom.trim() : inviteForm.kitchen_role;
    const email = inviteForm.email.trim();
    const name = inviteForm.display_name.trim();
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) { setToast('Email non valida'); return; }
    if (!name) { setToast('Indica il nome'); return; }
    if (!role) { setToast('Indica il ruolo'); return; }
    setInviteSending(true);
    setInviteUrl(null);
    try {
      const { data, error } = await getSupabase().functions.invoke('invite-staff', {
        body: {
          email,
          display_name: name,
          kitchen_role: role,
          app_role: inviteForm.app_role,
          attestato_haccp_scadenza: inviteForm.attestato || null,
        },
      });
      if (error) throw error;
      const url = (data as { invite_url?: string })?.invite_url ?? null;
      if (!url) throw new Error('invite_url mancante nella risposta');
      setInviteUrl(url);
    } catch (err) {
      setToast(`Errore invito: ${toUserMessage(err, 'sconosciuto')}`);
    } finally {
      setInviteSending(false);
    }
  }

  async function copyInviteUrl(): Promise<void> {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setToast('Link copiato negli appunti');
    } catch {
      setToast('Copia fallita — seleziona manualmente il link');
    }
  }

  function shareInvite(): void {
    if (!inviteUrl) return;
    const text = `Ciao ${inviteForm.display_name.trim()}, ti invito a unirti a TrackEAT come ${inviteForm.kitchen_role === '__custom__' ? inviteForm.kitchen_role_custom : inviteForm.kitchen_role}. Apri questo link per accettare:\n\n${inviteUrl}`;
    if (typeof navigator.share === 'function') {
      void shareWithNavigator({ title: 'Invito TrackEAT', text }).catch(() => undefined);
    } else {
      void navigator.clipboard.writeText(text).then(
        () => setToast('Messaggio copiato — incollalo su WhatsApp/email'),
        () => setToast('Copia fallita'),
      );
    }
  }

  function resetInviteForm(): void {
    setInviteForm({ email: '', display_name: '', kitchen_role: 'Cuoco', kitchen_role_custom: '', app_role: 'staff', attestato: '' });
    setInviteUrl(null);
  }

  async function archive(s: StaffProfile): Promise<void> {
    try {
      await enqueue.mutateAsync({
        table: 'staff_profiles',
        id: s.id,
        op: 'archive',
        base_version: s.version ?? 0,
        patch: { id: s.id },
      });
      setToast('Membro archiviato');
      setConfirmArchive(null);
    } catch (err) { setToast(toUserMessage(err)); }
  }

  function expBadge(date: string | null): { color: string; text: string } | null {
    if (!date) return null;
    const days = Math.floor((new Date(date).getTime() - Date.now()) / 86_400_000);
    if (days < 0) return { color: '#dc2626', text: `Scaduto ${-days}g fa` };
    if (days < 30) return { color: '#d97706', text: `Scade tra ${days}g` };
    if (days < 90) return { color: '#0ea5e9', text: `${days}g` };
    return { color: '#15803d', text: `${days}g` };
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/settings" />
          </IonButtons>
          <IonTitle>Personale</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <div style={{ padding: '12px 16px 96px' }}>
          {!isAdmin && (
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: 12, fontSize: 12, color: '#9a3412', marginBottom: 12 }}>
              Solo il titolare/consulente HACCP può modificare l'elenco del personale.
            </div>
          )}
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '4px 4px 12px' }}>
            Membri attivi · {visibleStaff.length}
          </h2>
          {visibleStaff.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 14, padding: 16, color: '#64748b', fontStyle: 'italic', fontSize: 13 }}>
              Nessun membro registrato. Tocca + per invitare il primo.
            </div>
          ) : (
            visibleStaff.map((s) => {
              const badge = expBadge(s.attestato_haccp_scadenza);
              return (
                <div
                  key={s.id}
                  onClick={() => isAdmin && openEdit(s)}
                  role={isAdmin ? 'button' : undefined}
                  style={{
                    background: '#fff', borderRadius: 14, padding: '14px 16px', marginBottom: 10,
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                    display: 'flex', alignItems: 'center', gap: 12,
                    cursor: isAdmin ? 'pointer' : 'default',
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 20, background: '#eef2f7',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <IonIcon icon={personOutline} style={{ fontSize: 20, color: '#475569' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{s.display_name}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{s.kitchen_role}</div>
                    {badge && (
                      <div style={{ fontSize: 11, color: badge.color, marginTop: 4 }}>
                        Attestato HACCP · {badge.text}
                      </div>
                    )}
                  </div>
                  {isAdmin && s.user_id !== profile?.id && (
                    <IonButton
                      fill="clear"
                      size="small"
                      color="danger"
                      onClick={(e) => { e.stopPropagation(); setConfirmArchive(s); }}
                      aria-label="Archivia"
                    >
                      <IonIcon icon={trashOutline} slot="icon-only" />
                    </IonButton>
                  )}
                </div>
              );
            })
          )}
          {profile?.role === 'staff' && (
            <div style={{ marginTop: 24, background: '#fff', borderRadius: 14, padding: '14px 16px', boxShadow: '0 1px 2px rgba(15,23,42,.06)' }}>
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>Il mio PIN tablet</h2>
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 10px', lineHeight: 1.45 }}>Scegli o modifica il tuo codice personale di 4 cifre. Il titolare o consulente HACCP può visualizzarlo per assistenza.</p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <IonInput value={myPin} type="password" inputmode="numeric" maxlength={4} placeholder="0000"
                  onIonInput={(event) => setMyPin(String(event.detail.value ?? '').replace(/\D/g, '').slice(0, 4))}
                  style={{ border: '1px solid #cbd5e1', borderRadius: 10, '--padding-start': '12px' } as React.CSSProperties} />
                <IonButton disabled={savingMyPin || myPin.length !== 4} onClick={() => { void saveMyPin(); }}>Salva</IonButton>
              </div>
            </div>
          )}
          {isAdmin && (
            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: '0 0 8px 4px' }}>
                <h2 style={{ flex: '1 1 140px', fontSize: 14, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>
                  PIN operatori tablet
                </h2>
                <IonButton size="small" fill="clear" onClick={openPersonnelTraining} style={{ minHeight: 44, margin: 0 }}>
                  <IonIcon icon={add} slot="start" /> Aggiungi personale
                </IonButton>
              </div>
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 4px 12px', lineHeight: 1.5 }}>
                Ogni persona usa il proprio PIN a 4 cifre sul tablet condiviso. Puoi mostrarlo, oppure rigenerarlo revocando subito quello precedente.
              </p>
              {canManageTabletMode && (
                <div style={{ background: '#f8fafc', borderRadius: 12, padding: '10px 12px', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, fontSize: 12, color: '#334155' }}><strong>Richiedi PIN sul tablet</strong><br />Se disattivato resta obbligatoria la scelta della persona, ma non il codice.</div>
                  <IonToggle checked={pinPolicy.data ?? true} disabled={savingPinPolicy || pinPolicy.isLoading}
                    onIonChange={(event) => { void changePinPolicy(!!event.detail.checked); }} />
                </div>
              )}
              {operatorRoster.isLoading ? (
                <div style={{ background: '#fff', borderRadius: 14, padding: 16, color: '#64748b', fontSize: 13 }}>Caricamento…</div>
              ) : (operatorRoster.data ?? []).length === 0 ? (
                <div style={{ background: '#fff', borderRadius: 14, padding: 16, color: '#475569', fontSize: 13, textAlign: 'center' }}>
                  <div style={{ marginBottom: 12 }}>Nessuna persona attiva. Collega lo staff già presente o inserisci il primo operatore.</div>
                  <IonButton onClick={openPersonnelTraining} style={{ minHeight: 44 }}>
                    <IonIcon icon={add} slot="start" /> Aggiungi personale
                  </IonButton>
                </div>
              ) : (
                (operatorRoster.data ?? []).map((operator) => (
                  <div
                    key={operator.tenant_personnel_id}
                    style={{
                      background: '#fff', borderRadius: 14, padding: '12px 14px', marginBottom: 10,
                      boxShadow: '0 1px 2px rgba(15,23,42,.06)', display: 'flex', alignItems: 'center', gap: 10,
                      flexWrap: 'wrap',
                    }}
                  >
                    <IonIcon icon={keyOutline} aria-hidden="true" style={{ fontSize: 22, color: operator.pin_enabled ? '#15803d' : '#94a3b8', flexShrink: 0 }} />
                    <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a' }}>{operator.full_name}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                        {operator.role} · {operator.pin_enabled ? 'PIN attivo' : 'PIN non impostato'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flex: '0 1 auto', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
                      <IonButton
                        size="small"
                        fill={operator.pin_enabled ? 'clear' : 'solid'}
                        disabled={pinBusyId === operator.tenant_personnel_id}
                        onClick={() => { void generateOperatorPin(operator.tenant_personnel_id); }}
                        style={{ minHeight: 44, margin: 0 }}
                      >
                        {pinBusyId === operator.tenant_personnel_id ? 'Attendi…' : operator.pin_enabled ? 'Rigenera' : 'Genera'}
                      </IonButton>
                      {operator.pin_enabled && (
                        <IonButton
                          size="small"
                          fill="outline"
                          disabled={pinBusyId === operator.tenant_personnel_id}
                          aria-label={`Mostra PIN di ${operator.full_name}`}
                          onClick={() => { void showOperatorPin(operator); }}
                          style={{ minHeight: 44, margin: 0 }}
                        >
                          Mostra PIN
                        </IonButton>
                      )}
                      {operator.pin_enabled && (
                      <IonButton
                        size="small"
                        fill="clear"
                        color="danger"
                        disabled={pinBusyId === operator.tenant_personnel_id}
                        aria-label={`Revoca PIN di ${operator.full_name}`}
                        onClick={() => setConfirmPinRevoke(operator)}
                        style={{ minHeight: 44, margin: 0 }}
                      >
                        Revoca
                      </IonButton>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          {isAdmin && (
            <div style={{ marginTop: 24 }}>
              <h2 style={{ fontSize: 14, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '4px 4px 12px' }}>
                Account & permessi · {accounts.length}
              </h2>
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 4px 12px' }}>
                Chi ha un accesso a questo locale, con email e permesso nell'app.
              </p>
              {accountsLoading ? (
                <div style={{ background: '#fff', borderRadius: 14, padding: 16, color: '#64748b', fontSize: 13 }}>
                  Caricamento…
                </div>
              ) : accounts.length === 0 ? (
                <div style={{ background: '#fff', borderRadius: 14, padding: 16, color: '#64748b', fontStyle: 'italic', fontSize: 13 }}>
                  Nessun account con accesso ancora.
                </div>
              ) : (
                accounts.map((a) => {
                  const b = APP_ROLE_BADGE[a.app_role] ?? { label: a.app_role, bg: '#f1f5f9', fg: '#475569' };
                  return (
                    <div
                      key={a.user_id}
                      style={{
                        background: '#fff', borderRadius: 14, padding: '14px 16px', marginBottom: 10,
                        boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                        display: 'flex', alignItems: 'center', gap: 12,
                      }}
                    >
                      <div style={{
                        width: 40, height: 40, borderRadius: 20, background: '#eef2f7',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <IonIcon icon={a.kind === 'preposto' ? mailOutline : personOutline} style={{ fontSize: 20, color: '#475569' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{a.full_name}</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.email}</div>
                        {a.destination_name && (
                          <div style={{ fontSize: 11, color: '#b45309', marginTop: 3 }}>Struttura · {a.destination_name}</div>
                        )}
                        {a.access_mode === 'shared_tablet' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#0369a1', marginTop: 4 }}>
                            <IonIcon icon={tabletLandscapeOutline} aria-hidden="true" />
                            Tablet staff condiviso
                          </div>
                        )}
                      </div>
                      <span style={{
                        fontSize: 11, fontWeight: 600, color: b.fg, background: b.bg,
                        borderRadius: 999, padding: '4px 10px', flexShrink: 0, textAlign: 'center',
                      }}>
                        {b.label}
                      </span>
                      {a.user_id !== profile?.id && a.app_role !== 'consulente' && (
                        <IonButton
                          fill="clear"
                          size="small"
                          style={{ margin: 0, '--padding-start': '2px', '--padding-end': '2px' } as React.CSSProperties}
                          onClick={() => setActionAccount(a)}
                          aria-label="Gestisci account"
                        >
                          <IonIcon icon={ellipsisVertical} slot="icon-only" style={{ color: '#94a3b8', fontSize: 18 }} />
                        </IonButton>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
          {isAdmin && (
            <div style={{ marginTop: 24 }}>
              <h2 style={{ fontSize: 14, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '4px 4px 12px' }}>
                Invita un nuovo membro
              </h2>
              <div style={{ background: '#fff', borderRadius: 14, padding: 16, boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
                <p style={{ fontSize: 13, color: '#475569', margin: '0 0 12px' }}>
                  <IonIcon icon={mailOutline} style={{ verticalAlign: 'middle', marginInlineEnd: 6, color: '#0066cc' }} />
                  Genera un link che il dipendente userà per registrarsi e accedere al ristorante.
                </p>
                <IonButton expand="block" onClick={() => { resetInviteForm(); setInviteOpen(true); }}>
                  <IonIcon icon={mailOutline} slot="start" />
                  Invita via link
                </IonButton>
              </div>
            </div>
          )}
        </div>

        {isAdmin && (
          <IonFab slot="fixed" vertical="bottom" horizontal="end">
            <IonFabButton onClick={openPersonnelTraining} aria-label="Aggiungi personale e formazione">
              <IonIcon icon={add} />
            </IonFabButton>
          </IonFab>
        )}

        <IonModal isOpen={editorOpen} onDidDismiss={() => setEditorOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{editing ? 'Modifica membro' : 'Nuovo membro'}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setEditorOpen(false)} aria-label="chiudi"><IonIcon icon={closeOutline} /></IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {!editing && (
              <IonText color="medium">
                <p style={{ fontSize: 13 }}>
                  Per ora puoi solo modificare i membri esistenti. L'invito di
                  nuovi dipendenti via email arriva nella prossima versione.
                </p>
              </IonText>
            )}
            <IonItem>
              <IonLabel position="stacked">Nome *</IonLabel>
              <IonInput
                value={form.display_name}
                placeholder="Es. Mario Rossi"
                onIonInput={(ev) => setForm((f) => ({ ...f, display_name: String(ev.detail.value ?? '') }))}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Ruolo in cucina/sala *</IonLabel>
              <IonSelect
                value={form.kitchen_role}
                onIonChange={(ev) => setForm((f) => ({ ...f, kitchen_role: String(ev.detail.value) }))}
              >
                {PREDEFINED_KITCHEN_ROLES.map((r) => (
                  <IonSelectOption key={r.value} value={r.value}>{r.label}</IonSelectOption>
                ))}
                <IonSelectOption value="__custom__">✏️ Altro (specifica)</IonSelectOption>
              </IonSelect>
            </IonItem>
            {form.kitchen_role === '__custom__' && (
              <IonItem>
                <IonLabel position="stacked">Ruolo personalizzato *</IonLabel>
                <IonInput
                  value={form.kitchen_role_custom}
                  placeholder="Es. Sommelier"
                  onIonInput={(ev) => setForm((f) => ({ ...f, kitchen_role_custom: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
            )}
            <IonItem>
              <IonLabel position="stacked">Scadenza attestato HACCP</IonLabel>
              <IonInput
                type="date"
                value={form.attestato}
                onIonInput={(ev) => setForm((f) => ({ ...f, attestato: String(ev.detail.value ?? '') }))}
              />
            </IonItem>
            <IonButton expand="block" style={{ marginTop: 16 }} onClick={() => { void onSave(); }}>
              {editing ? 'Salva modifiche' : 'Crea'}
            </IonButton>
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={!!confirmArchive}
          header="Archivia membro"
          message={confirmArchive ? `Archiviare "${confirmArchive.display_name}"? Non potrà più essere assegnato a task.` : ''}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            { text: 'Archivia', role: 'destructive', handler: () => { if (confirmArchive) void archive(confirmArchive); } },
          ]}
          onDidDismiss={() => setConfirmArchive(null)}
        />

        <IonAlert
          isOpen={!!confirmPinRevoke}
          header="Revoca PIN operatore"
          message={confirmPinRevoke
            ? `Revocare il PIN di “${confirmPinRevoke.full_name}”? Non potrà più sbloccare il tablet finché non ne generi uno nuovo.`
            : ''}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            {
              text: 'Revoca',
              role: 'destructive',
              handler: () => { if (confirmPinRevoke) void revokeOperatorPin(confirmPinRevoke); },
            },
          ]}
          onDidDismiss={() => setConfirmPinRevoke(null)}
        />

        <IonAlert
          isOpen={!!confirmLegacyPinRegeneration}
          header="PIN precedente non visualizzabile"
          message={confirmLegacyPinRegeneration
            ? `Il PIN di “${confirmLegacyPinRegeneration.full_name}” è stato creato prima della funzione Mostra PIN. Rigeneralo una sola volta: il vecchio codice verrà revocato e, se è collegato a un account staff, il nuovo sarà inviato automaticamente via email.`
            : ''}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            {
              text: 'Rigenera e invia',
              handler: () => {
                const operator = confirmLegacyPinRegeneration;
                setConfirmLegacyPinRegeneration(null);
                if (operator) void generateOperatorPin(operator.tenant_personnel_id);
              },
            },
          ]}
          onDidDismiss={() => setConfirmLegacyPinRegeneration(null)}
        />

        <OperatorPinModal value={visiblePin} onDismiss={() => setVisiblePin(null)} />

        {/* Modale invito staff: form + URL generato + bottoni share/copy. */}
        <IonModal isOpen={inviteOpen} onDidDismiss={() => setInviteOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Invita un membro</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setInviteOpen(false)} aria-label="chiudi"><IonIcon icon={closeOutline} /></IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {!inviteUrl ? (
              <>
                <IonText color="medium">
                  <p style={{ fontSize: 13, marginBottom: 12 }}>
                    Compila i dati e tocca "Genera link". Il dipendente aprirà il
                    link sul suo telefono, si registrerà con email + password, e
                    sarà aggiunto automaticamente al ristorante col ruolo scelto.
                  </p>
                </IonText>
                <IonItem>
                  <IonLabel position="stacked">Email *</IonLabel>
                  <IonInput
                    type="email"
                    inputmode="email"
                    autocapitalize="off"
                    value={inviteForm.email}
                    placeholder="mario.rossi@email.it"
                    onIonInput={(ev) => setInviteForm((f) => ({ ...f, email: String(ev.detail.value ?? '') }))}
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Nome e cognome *</IonLabel>
                  <IonInput
                    value={inviteForm.display_name}
                    placeholder="Mario Rossi"
                    onIonInput={(ev) => setInviteForm((f) => ({ ...f, display_name: String(ev.detail.value ?? '') }))}
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Ruolo operativo *</IonLabel>
                  <IonSelect
                    value={inviteForm.kitchen_role}
                    onIonChange={(ev) => setInviteForm((f) => ({ ...f, kitchen_role: String(ev.detail.value) }))}
                  >
                    {PREDEFINED_KITCHEN_ROLES.map((r) => (
                      <IonSelectOption key={r.value} value={r.value}>{r.label}</IonSelectOption>
                    ))}
                    <IonSelectOption value="__custom__">✏️ Altro</IonSelectOption>
                  </IonSelect>
                </IonItem>
                {inviteForm.kitchen_role === '__custom__' && (
                  <IonItem>
                    <IonLabel position="stacked">Ruolo personalizzato *</IonLabel>
                    <IonInput
                      value={inviteForm.kitchen_role_custom}
                      onIonInput={(ev) => setInviteForm((f) => ({ ...f, kitchen_role_custom: String(ev.detail.value ?? '') }))}
                    />
                  </IonItem>
                )}
                <IonItem>
                  <IonLabel position="stacked">Permesso applicazione *</IonLabel>
                  <IonSelect
                    value={inviteForm.app_role}
                    onIonChange={(ev) => setInviteForm((f) => ({ ...f, app_role: ev.detail.value as typeof f.app_role }))}
                  >
                    <IonSelectOption value="staff">Staff (esegue task assegnati)</IonSelectOption>
                    <IonSelectOption value="osa">OSA (gestisce HACCP del ristorante)</IonSelectOption>
                    <IonSelectOption value="ristoratore">Ristoratore (gestisce tutto, può invitare staff)</IonSelectOption>
                    <IonSelectOption value="consulente">Consulente HACCP</IonSelectOption>
                  </IonSelect>
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Scadenza attestato HACCP</IonLabel>
                  <IonInput
                    type="date"
                    value={inviteForm.attestato}
                    onIonInput={(ev) => setInviteForm((f) => ({ ...f, attestato: String(ev.detail.value ?? '') }))}
                  />
                </IonItem>
                <IonButton expand="block" style={{ marginTop: 16 }} disabled={inviteSending} onClick={() => { void sendInvite(); }}>
                  {inviteSending ? 'Genero link…' : 'Genera link di invito'}
                </IonButton>
              </>
            ) : (
              <>
                <IonText color="success">
                  <p style={{ fontSize: 14, fontWeight: 600 }}>✓ Link generato!</p>
                </IonText>
                <p style={{ fontSize: 13, color: '#475569' }}>
                  Condividi questo link con <b>{inviteForm.display_name}</b> via WhatsApp,
                  email o SMS. Il link è valido 14 giorni e usabile una sola volta.
                </p>
                <div style={{ background: '#f1f5f9', borderRadius: 8, padding: 10, fontSize: 11, wordBreak: 'break-all', fontFamily: 'monospace', marginBlock: 12 }}>
                  {inviteUrl}
                </div>
                <IonButton expand="block" onClick={() => { void copyInviteUrl(); }}>
                  <IonIcon icon={copyOutline} slot="start" />
                  Copia link
                </IonButton>
                <IonButton expand="block" fill="outline" style={{ marginTop: 8 }} onClick={() => shareInvite()}>
                  <IonIcon icon={mailOutline} slot="start" />
                  Condividi via WhatsApp / email
                </IonButton>
                <IonButton expand="block" fill="clear" style={{ marginTop: 8 }} onClick={() => { resetInviteForm(); }}>
                  Genera un altro invito
                </IonButton>
              </>
            )}
          </IonContent>
        </IonModal>

        <IonActionSheet
          isOpen={!!actionAccount}
          header={actionAccount ? `${actionAccount.full_name} · ${actionAccount.email}` : ''}
          onDidDismiss={() => setActionAccount(null)}
          buttons={actionAccount ? [
            ...(canManageTabletMode
              && actionAccount.kind === 'member'
              && actionAccount.app_role === 'staff'
              ? [{
                  text: actionAccount.access_mode === 'shared_tablet'
                    ? 'Disattiva modalità tablet'
                    : 'Trasforma in tablet staff',
                  handler: () => {
                    void setAccountAccessMode(
                      actionAccount,
                      actionAccount.access_mode === 'shared_tablet' ? 'personal' : 'shared_tablet',
                    );
                  },
                }]
              : []),
            ...(actionAccount.kind === 'member'
              ? (['ristoratore', 'osa', 'staff'] as const)
                  .filter((r) => r !== actionAccount.app_role)
                  .map((r) => ({
                    text: `Imposta ${APP_ROLE_BADGE[r].label}`,
                    handler: () => { void runAccountAction(actionAccount, 'set_role', r); },
                  }))
              : []),
            {
              text: actionAccount.kind === 'preposto' ? 'Rimuovi accesso' : 'Rimuovi dal locale',
              role: 'destructive',
              handler: () => { setConfirmRemove(actionAccount); },
            },
            { text: 'Annulla', role: 'cancel' },
          ] : []}
        />

        <IonAlert
          isOpen={!!confirmRemove}
          header="Rimuovi accesso"
          message={confirmRemove
            ? `Rimuovere l'accesso di "${confirmRemove.email}"? Non potrà più entrare con questo account. L'account non viene cancellato del tutto, solo scollegato dal locale.`
            : ''}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            { text: 'Rimuovi', role: 'destructive', handler: () => { if (confirmRemove) void runAccountAction(confirmRemove, 'remove'); } },
          ]}
          onDidDismiss={() => setConfirmRemove(null)}
        />

        <IonToast isOpen={!!toast} message={toast ?? ''} duration={1800} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
